import { attr, customElement, FASTElement, observable } from '@microsoft/fast-element';
import { DrilldownNodalLmeGraphStyles as styles } from './drilldown-nodal-lme-graph.styles';
import { DrilldownNodalLmeGraphTemplate as template } from './drilldown-nodal-lme-graph.template';
import Highcharts, { Chart } from 'highcharts';
import { getDatesFromData, getValuesFromData } from '../../../../../utils/chart-data-utils';
import { IProjectHistoricalTs, VppaRankings } from '../../../../../types';
import { AGGREGATION_TYPE, GRAPH_PERIOD_TIME, GRAPH_PERIODICITY, logger } from '../../../../../utils';
import { IVppaService } from '../../../../../services';
import { notifyError } from '../../../../../utils/notify';
import { getFormattedNumber } from "../../../../../utils/formatting";

const name = 'drilldown-nodal-lme-graph';

@customElement({
  name,
  template,
  styles,
})
export class DrilldownNodalLmeGraph extends FASTElement {
  @IVppaService vppaService: IVppaService;
  @attr @observable timePeriod: GRAPH_PERIOD_TIME = GRAPH_PERIOD_TIME.THREE_YEAR;
  @observable historicalDataArray: Array<any> = [];
  @observable nodalLmeHistoricalChart!: HTMLElement;
  @observable historicalData: Array<IProjectHistoricalTs>;
  @observable periodicity: GRAPH_PERIODICITY = GRAPH_PERIODICITY.QUATERLY;
  @observable projectNames: Array<String> = [];
  @observable chart: Chart;
  @observable colors = {};

  connectedCallback() {
    super.connectedCallback();
  }

  public renderGraph(historicalDataArray, projectNames) {
    this.historicalDataArray = historicalDataArray;
    this.projectNames = projectNames;

    this.renderNodalLmeHistorical();
  }

  private getDataArrays(projectNames): Array<any> {
    const dataArrays: any[] = [];
    if (this.historicalDataArray.length) {
      this.historicalDataArray.forEach((historicalData, index) => {
        dataArrays.push({
          type: 'line',
          zIndex: 2,
          color: historicalData.length ? this.colors[historicalData[0].VPPA_ID] : '',
          yAxis: 0,
          name: projectNames[index],
          showInLegend: false,
          data: getValuesFromData(
            historicalData,
            'LME_ASGEN_NODE_MT_CO_2E_PER_MWH',
            this.periodicity,
            this.periodicity !== GRAPH_PERIODICITY.MONTHLY ? AGGREGATION_TYPE.AVG : null
          ),
        });
      });
    }

    return dataArrays;
  }

  private renderNodalLmeHistorical() {
    this.periodicity = this.timePeriod === GRAPH_PERIOD_TIME.ONE_YEAR ? GRAPH_PERIODICITY.MONTHLY : GRAPH_PERIODICITY.QUATERLY;

    let dates = [];
    if (this.historicalDataArray.length) {
      dates = this.mergeVppaDates();
    }

    const dataArrays = this.getDataArrays(this.projectNames);

    this.chart = Highcharts.chart({
      chart: {
        renderTo: this.nodalLmeHistoricalChart,
        animation: false,
      },
      title: {
        text: '',
      },
      boost: {
          seriesThreshold: 5,
          usePreallocated: true,
          useGPUTranslations: true,
      },
      xAxis: {
        categories: dates,
      },
      yAxis: [
        {
          // Y-Axis
          title: {
            text: 'MT CO2/MWh',
          },
        },
      ],
      tooltip: {
        enabled: true,
        formatter: function () {
          let tooltip = this.x.toString();
          this.points?.forEach((point) => {
            const colorMarker = `<span style="color: ${point.color};">&#9679;</span>`;
            tooltip +=
              '<br>' +
              colorMarker +
              ` ${point.series.name} : ${
                getFormattedNumber(point.y, '', 2)
              }`;
          });
          return tooltip;
        },
      },
      series: dataArrays,
      plotOptions: {
        series: {
          animation: false,
        },
      },
    });

    this.reflow();
  }

  public reflow() {
    if (this.chart) {
      setTimeout(() => {
        this.chart.reflow();
      }, 0);
    }
  }

  private mergeVppaDates() {
    let dateArrays = [];
      this.historicalDataArray.forEach((array) => {
        const _dates = getDatesFromData(array, this.periodicity);
        dateArrays = dateArrays.concat(_dates.filter((date) => dateArrays.indexOf(date) < 0));
      });
      return dateArrays;
  }
}
